import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import html2pdf from "html2pdf.js";
import { closeModal } from "../../../../redux/actions/action-creator";

const ExportFormat = ({ data, sourceRef }) => {
  const dispatch = useDispatch();
  const searchInputRef = useRef(null);
  const boxRef = useRef(null);
  const sourceContentRef = sourceRef || boxRef;

  const [state, setState] = useState({ search_input: "" });
  const [selectedItem, setSelectedItem] = useState(0);

  const options = useMemo(
    () => [
      { id: "pdf", label: "As PDF" },
      { id: "excel", label: "As Excel" },
    ],
    [],
  );

  const filteredOptions = useMemo(() => {
    const query = String(state.search_input || "").toLowerCase();
    if (!query) return options;
    return options.filter((item) =>
      String(item.label || "")
        .toLowerCase()
        .includes(query),
    );
  }, [options, state.search_input]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const downloadAsPDF = useCallback(() => {
    try {
      const sourceNode = sourceContentRef.current;
      if (sourceNode) {
        const clonedNode = sourceNode.cloneNode(true);
        const wrapper = document.createElement("div");
        wrapper.style.width = "794px";
        wrapper.style.minHeight = "1123px";
        wrapper.style.background = "#fff";
        wrapper.style.padding = "12px";
        wrapper.style.boxSizing = "border-box";
        wrapper.appendChild(clonedNode);

        const fileName = `${String(data?.voucher_type_name || "ledger_statement").replace(/\s+/g, "_")}_${String(data?.serial_no || "export")}.pdf`;
        html2pdf()
          .set({
            margin: 0,
            filename: fileName,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { orientation: "portrait", unit: "mm", format: "a4" },
          })
          .from(wrapper)
          .save();
        return;
      }

      const rows = Array.isArray(data?.voucherEntryList)
        ? data.voucherEntryList
        : Array.isArray(data?.data_item)
          ? data.data_item
          : [];
      const validRows = rows.filter((row) =>
        String(row?.item_name || "").trim(),
      );

      // Format functions (same as Print.js)
      const formatInr = (value) => {
        const num = Number(String(value || "0").replace(/,/g, "")) || 0;
        return num.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      };

      const formatQuantity = (value) => {
        const num = Number(value) || 0;
        const hasFraction = Math.abs(num % 1) > 0;
        return num.toLocaleString("en-IN", {
          minimumFractionDigits: hasFraction ? 2 : 0,
          maximumFractionDigits: 2,
        });
      };

      const totalAmount = validRows.reduce((acc, item) => {
        const normalized = String(item?.item_amt || "0").replace(/,/g, "");
        return acc + (Number(normalized) || 0);
      }, 0);

      const totalQuantity = validRows.reduce((acc, item) => {
        const qtyText = String(item?.item_qty || "");
        const match = qtyText.match(/-?\d+(?:\.\d+)?/);
        const qtyValue = match ? Number(match[0]) : 0;
        return acc + (Number.isFinite(qtyValue) ? qtyValue : 0);
      }, 0);

      const htmlContent = document.createElement("div");
      htmlContent.style.width = "794px";
      htmlContent.style.minHeight = "1123px";
      htmlContent.style.background = "#fff";
      htmlContent.style.padding = "18px";
      htmlContent.style.fontSize = "12px";
      htmlContent.style.color = "#111";
      htmlContent.style.fontFamily = "Arial, sans-serif";

      // Company Header
      const companyHeader = document.createElement("div");
      companyHeader.style.textAlign = "center";
      companyHeader.style.fontWeight = "700";
      companyHeader.style.fontSize = "18px";
      companyHeader.innerHTML = String(data?.company_name || "Company Name");
      htmlContent.appendChild(companyHeader);

      // Company Address
      const companyAddress = document.createElement("div");
      companyAddress.style.textAlign = "center";
      companyAddress.style.marginTop = "2px";
      companyAddress.innerHTML = String(
        data?.company_address || "Address not available",
      );
      htmlContent.appendChild(companyAddress);

      // Company Contact
      const companyContact = document.createElement("div");
      companyContact.style.textAlign = "center";
      companyContact.style.marginTop = "2px";
      const mobile = String(data?.mobile || "");
      const email = String(data?.email || "");
      const gst = String(data?.gst_no || data?.gstin || "");
      const contactText = [
        mobile && `Mobile: ${mobile}`,
        email && `Email: ${email}`,
        gst && `GST: ${gst}`,
      ]
        .filter(Boolean)
        .join(" | ");
      companyContact.innerHTML = contactText || "Contact details not available";
      htmlContent.appendChild(companyContact);

      // Invoice Title
      const invoiceTitle = document.createElement("div");
      invoiceTitle.style.textAlign = "center";
      invoiceTitle.style.fontWeight = "900";
      invoiceTitle.style.fontSize = "14px";
      invoiceTitle.style.marginTop = "8px";
      invoiceTitle.style.borderTop = "1px solid #000";
      invoiceTitle.style.borderBottom = "1px solid #000";
      invoiceTitle.style.padding = "4px 0";
      invoiceTitle.innerHTML = String(data?.voucher_type_name || "Voucher");
      htmlContent.appendChild(invoiceTitle);

      // Shipping and Invoice Details Grid
      const gridContainer = document.createElement("div");
      gridContainer.style.display = "grid";
      gridContainer.style.gridTemplateColumns = "1fr 1fr";
      gridContainer.style.gap = "8px";
      gridContainer.style.marginTop = "8px";

      // Ship To Section
      const shipToDiv = document.createElement("div");
      shipToDiv.style.border = "1px solid #000";
      shipToDiv.style.padding = "8px";
      shipToDiv.style.minHeight = "200px";
      shipToDiv.style.textAlign = "left";

      const shipToTitle = document.createElement("div");
      shipToTitle.style.fontWeight = "700";
      shipToTitle.style.marginBottom = "4px";
      shipToTitle.innerHTML = "Consignee (Ship to)";
      shipToDiv.appendChild(shipToTitle);

      const shipToName = document.createElement("div");
      shipToName.style.paddingLeft = "12px";
      shipToName.innerHTML = String(
        data?.party_ship_to_mailing_name || data?.mailing_name_ship_to || "",
      );
      shipToDiv.appendChild(shipToName);

      const shipToAddress = document.createElement("div");
      shipToAddress.style.paddingLeft = "12px";
      shipToAddress.innerHTML = String(
        data?.ship_to_city || data?.city_ship_to || "",
      );
      shipToDiv.appendChild(shipToAddress);

      const shipToContact = document.createElement("div");
      shipToContact.style.paddingLeft = "12px";
      shipToContact.innerHTML = String(
        data?.ship_to_contact || data?.contact_ship_to || "",
      );
      shipToDiv.appendChild(shipToContact);

      // Bill To Section
      const billToDiv = document.createElement("div");
      billToDiv.style.borderTop = "1px solid #000";
      billToDiv.style.marginTop = "8px";
      billToDiv.style.paddingTop = "6px";
      billToDiv.style.textAlign = "left";

      const billToTitle = document.createElement("div");
      billToTitle.style.fontWeight = "700";
      billToTitle.style.marginBottom = "4px";
      billToTitle.innerHTML = "Buyer (Bill to)";
      billToDiv.appendChild(billToTitle);

      const billToName = document.createElement("div");
      billToName.style.paddingLeft = "12px";
      billToName.innerHTML = String(
        data?.party_bill_to_mailing_name || data?.mailing_name_bill_to || "",
      );
      billToDiv.appendChild(billToName);

      const billToAddress = document.createElement("div");
      billToAddress.style.paddingLeft = "12px";
      billToAddress.innerHTML = String(
        data?.bill_to_city || data?.city_bill_to || "",
      );
      billToDiv.appendChild(billToAddress);

      const billToContact = document.createElement("div");
      billToContact.style.paddingLeft = "12px";
      billToContact.innerHTML = String(
        data?.bill_to_contact || data?.contact_bill_to || "",
      );
      billToDiv.appendChild(billToContact);

      shipToDiv.appendChild(billToDiv);
      gridContainer.appendChild(shipToDiv);

      // Invoice Details Section
      const invoiceDetailsDiv = document.createElement("div");
      invoiceDetailsDiv.style.border = "1px solid #000";
      invoiceDetailsDiv.style.padding = "8px";
      invoiceDetailsDiv.style.minHeight = "200px";
      invoiceDetailsDiv.style.textAlign = "left";

      const detailsGrid = document.createElement("div");
      detailsGrid.style.display = "grid";
      detailsGrid.style.gridTemplateColumns = "44% 56%";
      detailsGrid.style.rowGap = "4px";

      const detailsData = [
        { label: "Invoice No.", value: String(data?.serial_no || "-") },
        { label: "Dated", value: String(data?.create_date || "-") },
        {
          label: "Delivery Note",
          value: String(data?.delivery_note_no || "-"),
        },
        {
          label: "Mode/Terms of Payment",
          value: String(data?.mode_term_payment || "-"),
        },
        {
          label: "Dispatched Through",
          value: String(data?.dispatch_through || "-"),
        },
        { label: "Destination", value: String(data?.destination || "-") },
        {
          label: "Dispatch Doc No.",
          value: String(data?.dispatch_doc_no || "-"),
        },
        {
          label: "Driver/Agent",
          value: String(data?.carrier_name_agent || "-"),
        },
        {
          label: "Mobile No.",
          value: String(
            data?.carrier_mobile_no ||
              data?.carrier_mobile_number ||
              data?.agent_mobile_no ||
              "-",
          ),
        },
        {
          label: "Vehical No.",
          value: String(data?.vehicle_number || data?.vehical_number || "-"),
        },
      ];

      detailsData.forEach((detail) => {
        const label = document.createElement("div");
        label.innerHTML = detail.label;
        detailsGrid.appendChild(label);

        const value = document.createElement("div");
        value.innerHTML = ": " + detail.value;
        detailsGrid.appendChild(value);
      });

      invoiceDetailsDiv.appendChild(detailsGrid);
      gridContainer.appendChild(invoiceDetailsDiv);
      htmlContent.appendChild(gridContainer);

      // Items Table
      const table = document.createElement("table");
      table.style.width = "100%";
      table.style.borderCollapse = "collapse";
      table.style.marginTop = "10px";

      const thead = document.createElement("thead");
      const headerRow = document.createElement("tr");

      const headers = [
        { text: "Sl", width: "6%" },
        { text: "Description of Goods", width: "38%" },
        { text: "Quantity", width: "12%" },
        { text: "Alt. Unit", width: "12%" },
        { text: "Rate", width: "12%" },
        { text: "Per", width: "8%" },
        { text: "Amount", width: "14%" },
      ];

      headers.forEach((header) => {
        const th = document.createElement("th");
        th.style.border = "1px solid #000";
        th.style.padding = "4px";
        th.style.width = header.width;
        th.innerHTML = header.text;
        headerRow.appendChild(th);
      });
      thead.appendChild(headerRow);
      table.appendChild(thead);

      // Table Body
      const tbody = document.createElement("tbody");
      validRows.forEach((row, idx) => {
        const tr = document.createElement("tr");

        const cells = [
          { text: String(idx + 1), align: "right" },
          { text: String(row?.item_name || ""), align: "left" },
          { text: String(row?.item_qty || ""), align: "right" },
          {
            text: row?.item_alternate_unit
              ? `(${row.item_alternate_unit})`
              : "",
            align: "right",
          },
          { text: formatInr(String(row?.item_rate || "0")), align: "right" },
          { text: String(row?.per_unit || ""), align: "center" },
          { text: formatInr(String(row?.item_amt || "0")), align: "right" },
        ];

        cells.forEach((cell) => {
          const td = document.createElement("td");
          td.style.border = "1px solid #000";
          td.style.padding = "4px";
          td.style.textAlign = cell.align;
          td.style.height = "26px";
          td.innerHTML = cell.text;
          tr.appendChild(td);
        });

        tbody.appendChild(tr);
      });

      // Total Row
      const totalRow = document.createElement("tr");
      const totalCells = [
        { text: "", align: "right" },
        { text: "Total", align: "right", fontWeight: "700" },
        {
          text: formatQuantity(totalQuantity),
          align: "right",
          fontWeight: "700",
        },
        { text: "", align: "right" },
        { text: "", align: "right" },
        { text: "", align: "right" },
        { text: formatInr(totalAmount), align: "right", fontWeight: "700" },
      ];

      totalCells.forEach((cell) => {
        const td = document.createElement("td");
        td.style.border = "1px solid #000";
        td.style.padding = "6px";
        td.style.textAlign = cell.align;
        if (cell.fontWeight) td.style.fontWeight = cell.fontWeight;
        td.innerHTML = cell.text;
        totalRow.appendChild(td);
      });
      tbody.appendChild(totalRow);
      table.appendChild(tbody);
      htmlContent.appendChild(table);

      // Narration and Payment Allocation
      const narrativeGrid = document.createElement("div");
      narrativeGrid.style.marginTop = "8px";
      narrativeGrid.style.display = "grid";
      narrativeGrid.style.gridTemplateColumns = "1fr 1fr";
      narrativeGrid.style.gap = "8px";

      const narrativeDiv = document.createElement("div");
      narrativeDiv.style.border = "1px solid #000";
      narrativeDiv.style.padding = "6px";
      narrativeDiv.style.minHeight = "96px";
      narrativeDiv.style.textAlign = "left";
      narrativeDiv.innerHTML = `<div><b>Narration</b> ${String(data?.narration || "-")}</div>`;
      narrativeGrid.appendChild(narrativeDiv);

      const paymentDiv = document.createElement("div");
      paymentDiv.style.border = "1px solid #000";
      paymentDiv.innerHTML = `<div style="padding: 6px 8px;"><div style="fontWeight: 700; marginBottom: 4px; textAlign: left;">Payment Allocation</div><div style="color: #333;">-</div></div>`;
      narrativeGrid.appendChild(paymentDiv);
      htmlContent.appendChild(narrativeGrid);

      // Net Payable
      const netPayableDiv = document.createElement("div");
      netPayableDiv.style.marginTop = "8px";
      netPayableDiv.style.border = "1px solid #000";
      netPayableDiv.style.padding = "6px 8px";
      netPayableDiv.style.display = "flex";
      netPayableDiv.style.justifyContent = "space-between";
      netPayableDiv.style.fontWeight = "700";
      netPayableDiv.innerHTML = `<span>Net Payable</span><span>${formatInr(totalAmount)}</span>`;
      htmlContent.appendChild(netPayableDiv);

      // Signature Section
      const signatureDiv = document.createElement("div");
      signatureDiv.style.marginTop = "8px";
      signatureDiv.style.border = "1px solid #000";
      signatureDiv.style.padding = "6px";
      signatureDiv.style.minHeight = "72px";
      signatureDiv.style.display = "flex";
      signatureDiv.style.justifyContent = "space-between";
      signatureDiv.style.alignItems = "flex-end";

      const receiverSpan = document.createElement("span");
      receiverSpan.innerHTML = "Receiver Signature";
      signatureDiv.appendChild(receiverSpan);

      const authDiv = document.createElement("div");
      authDiv.style.display = "flex";
      authDiv.style.flexDirection = "column";
      authDiv.style.alignItems = "flex-end";
      authDiv.style.justifyContent = "space-between";
      authDiv.style.minHeight = "56px";

      const companyNameSpan = document.createElement("span");
      companyNameSpan.style.fontWeight = "700";
      companyNameSpan.innerHTML = `For ${String(data?.company_name || "Company Name")}`;
      authDiv.appendChild(companyNameSpan);

      const authSpan = document.createElement("span");
      authSpan.innerHTML = "Authorised Signatory";
      authDiv.appendChild(authSpan);

      signatureDiv.appendChild(authDiv);
      htmlContent.appendChild(signatureDiv);

      // Generate PDF using html2pdf
      const fileName = `${String(data?.voucher_type_name || "voucher").replace(/\s+/g, "_")}_${String(data?.serial_no || "export")}.pdf`;

      const options = {
        margin: 0,
        filename: fileName,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { orientation: "portrait", unit: "mm", format: "a4" },
      };

      console.log("Generating PDF with professional invoice format...");
      html2pdf().set(options).from(htmlContent).save();
      console.log("PDF generated successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Check console for details.");
    }
  }, [data]);

  const downloadAsExcelCsv = useCallback(() => {
    const rows = Array.isArray(data?.data_item) ? data.data_item : [];
    const validRows = rows.filter((row) => String(row?.item_name || "").trim());

    const header = [
      "Voucher Type",
      "Voucher No",
      "Date",
      "Item",
      "Quantity",
      "Rate",
      "Per",
      "Amount",
    ];

    const escapeCell = (value) => {
      const text = String(value ?? "");
      if (text.includes(",") || text.includes("\n") || text.includes('"')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const csvRows = [header.join(",")];
    if (validRows.length === 0) {
      csvRows.push(
        [
          data?.voucher_type_name || "",
          data?.serial_no || "",
          data?.create_date || "",
          "",
          "",
          "",
          "",
          data?.total_amount || "",
        ]
          .map(escapeCell)
          .join(","),
      );
    } else {
      validRows.forEach((row) => {
        csvRows.push(
          [
            data?.voucher_type_name || "",
            data?.serial_no || "",
            data?.create_date || "",
            row?.item_name || "",
            row?.item_qty || "",
            row?.item_rate || "",
            row?.per_unit || "",
            row?.item_amt || "",
          ]
            .map(escapeCell)
            .join(","),
        );
      });
    }

    const csvContent = `\uFEFF${csvRows.join("\n")}`;
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const fileName = `${String(data?.voucher_type_name || "voucher").replace(/\s+/g, "_")}_${String(data?.serial_no || "export")}.csv`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }, [data]);

  const openSelectedOption = useCallback(
    (index = selectedItem) => {
      const selected = filteredOptions[index];
      if (!selected) return;

      if (selected.id === "pdf") {
        downloadAsPDF();
        // Add small delay before closing modal to ensure PDF generation completes
        setTimeout(() => {
          dispatch(closeModal("export"));
        }, 500);
        return;
      }

      if (selected.id === "excel") {
        downloadAsExcelCsv();
        dispatch(closeModal("export"));
      }
    },
    [
      dispatch,
      downloadAsPDF,
      downloadAsExcelCsv,
      filteredOptions,
      selectedItem,
    ],
  );

  useEffect(() => {
    if (boxRef.current) {
      const selectedElement = boxRef.current.querySelector(".selected");
      if (selectedElement) {
        const boxHeight = boxRef.current.offsetHeight;
        const elementBottom = selectedElement.offsetTop - 60;
        const elementBottom1 =
          selectedElement.offsetTop + selectedElement.offsetHeight;
        if (elementBottom > boxHeight) {
          boxRef.current.scrollTop = elementBottom - boxHeight;
        } else if (elementBottom1 > boxHeight) {
          boxRef.current.scrollTop = elementBottom - boxHeight;
        }
      }
    }
  }, [selectedItem]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        dispatch(closeModal("export"));
        e.stopPropagation();
        return;
      }

      if (!filteredOptions.length) return;

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedItem((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedItem((prev) =>
          Math.min(prev + 1, filteredOptions.length - 1),
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        openSelectedOption(selectedItem);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [dispatch, filteredOptions.length, openSelectedOption, selectedItem]);

  return (
    <div className="my-card">
      <div
        className="cross_button"
        onClick={() => {
          dispatch(closeModal("export"));
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          className="bi bi-x"
          viewBox="0 0 16 16"
        >
          <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
        </svg>
      </div>

      <div className="container">
        <div className="row justify-content-center mt-4">
          <div className="col-12">
            <div className="row justify-content-center">
              <div
                className="col-4 border bg-light border-dark"
                style={{ height: "85vh", overflowY: "scroll" }}
              >
                <div className="row">
                  <div
                    className="col-12"
                    style={{ maxHeight: "75vh", overflowY: "scroll" }}
                  >
                    <div className="row background" style={{ height: 19.5 }}>
                      <div className="col-12 text-left">
                        <p className="text-start"> List of Export Format</p>
                      </div>
                    </div>
                    <div
                      className="row ps-1 pe-1 pt-1"
                      style={{ height: 19.5 }}
                    >
                      <input
                        autoFocus
                        autoComplete="off"
                        className="voucher_entry_account_input"
                        type="text"
                        id="search_input"
                        name="search_input"
                        style={{ margin: 0, padding: 0, paddingLeft: 5 }}
                        onKeyUp={handleChange}
                        onChange={handleChange}
                        ref={searchInputRef}
                        value={state.search_input || ""}
                      />
                    </div>
                    <div
                      className="row mt-1 border-top border-secondary"
                      ref={boxRef}
                    >
                      {filteredOptions.length > 0 ? (
                        filteredOptions.map((item, index) => {
                          const matchIndex = item.label
                            .toLowerCase()
                            .indexOf(state.search_input.toLowerCase());

                          return (
                            <div
                              className="col-12 p-0 dropdown_link"
                              key={item.id}
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectedItem(index);
                                if (index === selectedItem) {
                                  openSelectedOption(index);
                                }
                              }}
                            >
                              <Link
                                className={`ps-2 ${index === selectedItem ? "selected" : ""}`}
                              >
                                {matchIndex !== -1 && state.search_input ? (
                                  <>
                                    {item.label.substring(0, matchIndex)}
                                    <span style={{ color: "red" }}>
                                      {item.label.substring(
                                        matchIndex,
                                        matchIndex + state.search_input.length,
                                      )}
                                    </span>
                                    {item.label.substring(
                                      matchIndex + state.search_input.length,
                                    )}
                                  </>
                                ) : (
                                  item.label
                                )}
                              </Link>
                            </div>
                          );
                        })
                      ) : (
                        <p style={{ color: "red" }}>No matching items found</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportFormat;
